import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button, Col, Row } from 'reactstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import CompletePopup from './completepopup';
import NoInternet from '../NoInternet';
import NoDataFound2 from '../NoData2';
import { useSnackbar } from "../context/SnackbarContext";

// img
import RotatePhonePopup from '../RotatePhonePopup';
import Nouser from "../../img/NoUserFound.png";
import NoNet from "../../img/NoNet.png";
import NouserDark from "../../img/NoUserFoundDark.png";
import NoNetDark from "../../img/NoNetDark.png";
import { useTranslatedText } from '../../hooks/useTranslatedText';
import { useLanguage } from '../context/Languagecontext';
import LanguageDropdown from '../Languagedropdown';
import { convertToNativeNumerals } from '../../utils/numberUtils';
import DiceButton from './DiceButton';
import BadWordWarning from './Badwordwarning';
import useBadWordDetection from '../../hooks/Usebadworddetection';
import useCardPreview from '../../hooks/useCardPreview';


const isSmallScreen = window.innerWidth <= 375 && window.innerWidth !== 360;

// SessionStorage helper functions
const saveToSessionStorage = (key, value) => {
    try {
        sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
        console.error('Error saving to sessionStorage:', error);
    }
};

// Skeleton Loader Component
// Updated Skeleton Loader Component
const CardSkeleton = ({ width }) => (
    <div
        className="shadow rounded-4 mx-auto p-0 d-flex flex-column justify-content-between position-relative overflow-hidden"
        style={{
            border: "3px solid rgba(0, 0, 0, 0.1)",
            width: width,
            minHeight: isSmallScreen ? "400px" : "480px",
        }}
    >
        <div
            className="shimmer-element"
            style={{
                height: "90px",
                width: "100%",
            }}
        >
        </div>

        <div
            className="shimmer-element mx-auto"
            style={{
                border: "1px solid rgba(0, 0, 0, 0.1)",
                borderRadius: "50%",
                width: "120px",
                height: "120px",
            }}
        />

        {/* Comment Box Section Skeleton */}
        <div className='d-flex align-items-center justify-content-center'>
            <div
                className='mx-auto rounded-4 shimmer-element'
                style={{
                    width: "90%",
                    minHeight: "90px",
                    border: "2px solid rgba(0, 0, 0, 0.1)",
                    marginBottom: "16px"
                }}
            >
            </div>
        </div>
    </div>
);

const getInitialDarkMode = () => {
    if (typeof window !== 'undefined') {
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
};


const ReputationCard = () => {
    const { lanText, t } = useLanguage();
    const location = useLocation();
    const { username, category } = useParams();
    const navigate = useNavigate();

    const reputationData = [
        { key: "veryhigh", title: t?.reputation?.veryhigh || "Very High", desc: "Top tier reputation!", color: "#EB7C69", image: "https://lol-image-bucket.s3.amazonaws.com/images/question4/Emoji/UserCardBg-a5129fb4-cdff-4b85-abac-95bf56d403f0.png" },
        { key: "high", title: t?.reputation?.high || "High", desc: "Strong reputation!", color: "#DA737E", image: "https://lol-image-bucket.s3.amazonaws.com/images/question4/Emoji/UserCardBg-a5129fb4-cdff-4b85-abac-95bf56d403f0.png" },
        { key: "good", title: t?.reputation?.good || "Good", desc: "Well respected!", color: "#C1659C", image: "https://lol-image-bucket.s3.amazonaws.com/images/question4/Emoji/UserCardBg-8142d87b-e222-4d8c-934f-15408c477442.png" },
        { key: "average", title: t?.reputation?.average || "Average", desc: "Normal reputation!", color: "#9A50CA", image: "https://lol-image-bucket.s3.amazonaws.com/images/question4/Emoji/UserCardBg-adb4eb64-4742-4693-b62e-db86e20769fe.png" },
        { key: "low", title: t?.reputation?.low || "Low", desc: "Needs improvement!", color: "#773EF4", image: "https://lol-image-bucket.s3.amazonaws.com/images/question4/Emoji/UserCardBg-4c58fdc1-33ab-463c-98d9-1360603831b4.png" },
    ];

    // FIXED: Prioritize fresh data from location.state over sessionStorage
    const getStateData = () => {
        // First priority: fresh navigation data
        if (location.state && Object.keys(location.state).length > 0) {
            sessionStorage.setItem('reputationFlowData', JSON.stringify(location.state));
            return location.state;
        }

        // Second priority: session storage
        const sessionData = sessionStorage.getItem('reputationFlowData');
        if (sessionData) {
            try {
                return JSON.parse(sessionData);
            } catch (error) {
                console.error('Error parsing session data:', error);
                return null;
            }
        }

        return null;
    };

    const stateData = getStateData();

    const { hint, uploadedImage, originalData, translatedQuestion, reputationKey } = stateData;
    const translatedQuestion2 = useTranslatedText(translatedQuestion, lanText);
    const translatedName = reputationData.find(r => r.key === reputationKey)?.title || "";
    const emojiImage = reputationData.find(r => r.key === reputationKey)?.image || "";


    // Check if success modal should be shown on initial load
    const urlParams = new URLSearchParams(window.location.search);
    const shouldShowModal = urlParams.get("success") === "1";

    const { loading, randomData, imageLoaded, noInternet, error } = useCardPreview({
        type: "reputation",
        shouldShowModal,
    });

    const [isUploading, setIsUploading] = useState(false);
    const [isShaking, setIsShaking] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(shouldShowModal);
    const [isCheckingConnection, setIsCheckingConnection] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(getInitialDarkMode);
    const [noNetDarkImgSrc, setNoNetDarkImgSrc] = useState(null);
    const [noNetImgSrc, setNoNetImgSrc] = useState(null);
    const [noNetImgLoaded, setNoNetImgLoaded] = useState(false);
    const [noUserDarkImgSrc, setNoUserDarkImgSrc] = useState(null);
    const [noUserImgLoaded, setNoUserImgLoaded] = useState(false);
    const [noUserImgSrc, setNoUserImgSrc] = useState(null);
    const [textareaClicked, setTextareaClicked] = useState(false);
    const [keyboardOpen, setKeyboardOpen] = useState(false);
    const [showContent, setShowContent] = useState(true);
    const [showContentButton, setShowContentButton] = useState(() => {
        const saved = sessionStorage.getItem("showContentButton");

        if (saved === null) {
            return true;
        }

        return saved === "true";
    });
    const [isButtonPressed, setIsButtonPressed] = useState(false);
    const [comment, setComment] = useState('');
    const { hasBadWord } = useBadWordDetection(comment);
    const textareaRef = useRef(null);
    const mainContainerRef = useRef(null);
    const topRef = useRef(null);

    const { showSnackbar } = useSnackbar();

    const cardWidth = () => {
        if (window.innerWidth <= 375) return '93%';
        return '360px';
    };


    const checkInternetConnection = async () => {
        await new Promise(resolve => setTimeout(resolve, 500));

        if (!navigator.onLine) {
            return false;
        }

        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000);
            clearTimeout(timeoutId);
            return true;
        } catch (error) {
            console.log('Network check failed:', error);
            return false;
        }
    };

    // Check dark mode
    useEffect(() => {
        const checkDarkMode = () => {
            const darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            setIsDarkMode(darkModeMediaQuery.matches);
        };

        checkDarkMode();

        const darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e) => setIsDarkMode(e.matches);

        darkModeMediaQuery.addEventListener('change', handleChange);

        return () => darkModeMediaQuery.removeEventListener('change', handleChange);
    }, []);

    const currentNoUserImg = isDarkMode ? noUserDarkImgSrc : noUserImgSrc;
    const currentNoNetImg = isDarkMode ? noNetDarkImgSrc : noNetImgSrc;

    // Load images from localStorage (written by CategoryLink)
    useEffect(() => {
        let isMounted = true;

        const loadImage = (key, fallbackImg, setter, setLoaded) => {
            const cached = localStorage.getItem(key);
            if (isMounted) {
                setter(cached || fallbackImg);
                if (setLoaded) setLoaded(true);
            }
        };

        loadImage('noUserImage', Nouser, setNoUserImgSrc, setNoUserImgLoaded);
        loadImage('noNetImage', NoNet, setNoNetImgSrc, setNoNetImgLoaded);
        loadImage('noUserImageDark', NouserDark, setNoUserDarkImgSrc);
        loadImage('noNetImageDark', NoNetDark, setNoNetDarkImgSrc);

        return () => { isMounted = false; }
    }, []);

    // Save location.state to sessionStorage on component mount
    useEffect(() => {
        if (location.state) {
            saveToSessionStorage('reputationCardState', location.state);
        }
    }, [location.state]);


    // Handle back button when modal is open
    useEffect(() => {
        const handlePopState = () => {
            if (showSuccessModal) {
                setShowSuccessModal(false);
                const url = new URL(window.location.href);
                if (url.searchParams.has("success")) {
                    url.searchParams.delete("success");
                    window.history.replaceState({}, "", url.pathname);
                }
            }
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [showSuccessModal]);

    // Check URL params for success modal
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);

        if (urlParams.get("success") === "1") {
            setShowSuccessModal(true);
        }
    }, []);

    useEffect(() => {
        if (!stateData || !stateData.translatedQuestion || !stateData.originalData) {
            console.log('Missing essential data, redirecting to start...');
            navigate(`/${username}/${category}`);
        }
    }, [stateData, username, category, navigate]);

    useEffect(() => {
        const isDisabled = isUploading || isCheckingConnection || textareaClicked;
        window.dispatchEvent(new CustomEvent('dropdownDisabledState', {
            detail: { disabled: isDisabled }
        }));
    }, [isUploading, isCheckingConnection, textareaClicked]);


    const handleButtonPress = () => {
        setIsButtonPressed(true);
    };

    const handleButtonRelease = () => {
        setIsButtonPressed(false);
    };

    const base64ToFile = (base64String, fileName) => {
        const arr = base64String.split(",");
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], fileName, { type: mime });
    };


    const upload = async () => {
        const deviceIp = localStorage.getItem("deviceIp");
        setIsUploading(true);

        try {
            const formData = new FormData();

            formData.append('id', originalData.id);
            formData.append('category', category);
            formData.append("webCategory", category);
            formData.append('hint', hint);

            if (uploadedImage) {
                if (uploadedImage.startsWith('data:image')) {
                    formData.append('uploadedImage', base64ToFile(uploadedImage, 'hintImage.jpg'));
                } else {
                    formData.append('uploadedImage', uploadedImage);
                }
            }


            if (comment && comment.trim() && showContent === true) {
                formData.append('reputationComment', comment.trim());
            }

            formData.append('reputationBg', randomData.CardBg);
            formData.append('reputationName', translatedName);
            formData.append('reputationEmoji', emojiImage);

            formData.append('userLocation', "allow");
            formData.append('deviceIp', deviceIp);

            await axios.post('https://api.lolcards.link/api/newinbox/create', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            // layout button hide
            setShowContentButton(false);
            sessionStorage.setItem("showContentButton", "false");

            const url = new URL(window.location.href);
            url.searchParams.set('success', '1');
            window.history.pushState({ success: true }, '', url.toString());
            setShowSuccessModal(true);
            setComment('')
        } catch (error) {
            console.error('Error uploading data:', error);
            // const errorMessage = error.response?.data?.message || 'Something went wrong while uploading.';
            showSnackbar(t?.common?.apiFail || 'Request failed. Please try again after some time!', "error");
        } finally {
            setIsUploading(false);
        }
    };

    const handleSendClick = async () => {
        if (!isUploading) {
            setIsCheckingConnection(true);

            const isOnline = await checkInternetConnection();

            setIsCheckingConnection(false);

            if (!isOnline) {
                showSnackbar(t?.common?.nointertoast || "Please turn on internet connection!", "error");
                return;
            }
            try {
                await upload();
                await axios.patch(
                    'https://api.lolcards.link/api/admin/monitoring',
                    { categoryname: category, id: originalData.id }
                );
            } catch (error) {
                console.error('Error updating monitoring:', error);
            }
        }
    };

    const handleModalClose = () => {
        setShowSuccessModal(false);
        // Clear all emotion flow data when successfully completed
        // sessionStorage.removeItem('emotionFlowData');
        // sessionStorage.removeItem('audioBlob');

        // Clear URL params
        const url = new URL(window.location.href);
        if (url.searchParams.has("success")) {
            url.searchParams.delete("success");
            window.history.replaceState({}, "", url.pathname);
        }
    };

    const handleKeyDown = (e) => {
        // On mobile keyboards, when user presses the "Done" button
        if (e.key === 'Enter') {
            e.preventDefault(); // Prevent default behavior (new line)

            // Blur the textarea to hide the keyboard
            e.target.blur();
        }
    };

    // 5. Update your handleTextareaClick function:
    const handleTextareaClick = () => {
        setTextareaClicked(true);
        setTimeout(() => {
            textareaRef.current?.focus();
        }, 0);
    };

    const maxLength = 100;
    const typedChars = [...comment].length;

    // Trigger shake animation - Added shake functionality
    const triggerShake = () => {
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 600);
    };

    const handleTextChange = (e) => {
        const newValue = e.target.value;
        const charCount = [...newValue].length; // Fix: count Unicode code points

        if (charCount > maxLength) {
            triggerShake();
            return;
        }

        setComment(newValue);
    };

    const scrollToTop = () => {
        setTimeout(() => {
            window.scrollTo(0, 0);
            if (mainContainerRef.current) mainContainerRef.current.scrollTop = 0;
            document.body.scrollTop = 0;
            document.documentElement.scrollTop = 0;
        }, 0);
    };

    // 3. Update your handleBlur function:
    const handleEditClick = () => {
        if (isUploading || isCheckingConnection) return;
        setTextareaClicked(true);

        setTimeout(() => {
            if (textareaRef.current) {
                const length = textareaRef.current.value.length;
                textareaRef.current.focus(); // prevent default scrolling
                textareaRef.current.setSelectionRange(length, length);
            }
        }, 0);
    };



    // Update handleBlur
    const handleBlur = () => {
        setKeyboardOpen(false);
        scrollToTop();
        setTextareaClicked(false);
    };

    // Add this ref at the top with other refs
    const scrollTimeoutRef = useRef(null);

    const handleFocus = (e) => {
        setKeyboardOpen(true);
        // Prevent default scroll behavior
        if (e && e.preventDefault) {
            e.preventDefault();
        }

        // Clear any pending scroll operations
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }

        // Scroll to top immediately to prevent keyboard push
        window.scrollTo(0, 0);
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;

        // Then scroll to topRef smoothly after a brief delay
        scrollTimeoutRef.current = setTimeout(() => {
            requestAnimationFrame(() => {
                if (topRef.current) {
                    const element = topRef.current;
                    const rect = element.getBoundingClientRect();
                    const scrollTop = window.scrollY || document.documentElement.scrollTop;
                    const targetY = rect.top + scrollTop;

                    window.scrollTo({
                        top: targetY,
                        behavior: "smooth"
                    });
                }
            });
        }, 100);
    };

    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

    if (noInternet) return <NoInternet style={{ height: '100vh' }} t={t.noData} Nouser={currentNoNetImg} imgLoaded={noNetImgLoaded} />;
    if (error) return <NoDataFound2 style={{ height: '100vh' }} t={t.noData2} Nouser={currentNoUserImg} username={username} category={category} imgLoaded={noUserImgLoaded} />;


    return (
        <div
            className="pt-3"
            style={{
                backgroundColor: loading || !imageLoaded ? (isDarkMode ? "#1a1a1a" : "#ffffff") : undefined,
                backgroundImage: !loading && imageLoaded ? `url(${randomData.CardBg})` : "none",
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
                backgroundPosition: "center",
                position: "relative",
                overflow: "hidden",
                height: "100dvh",
            }}
        >
            {/* BLUR OVERLAY */}
            <div
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    background: loading || !imageLoaded
                        ? (isDarkMode ? "rgba(26, 26, 26, 0.95)" : "rgba(255, 255, 255, 0.95)")
                        : "rgba(0, 0, 0, 0.15)",
                    backdropFilter: "blur(11px)",
                    WebkitBackdropFilter: "blur(11px)",
                    zIndex: 1,
                }}
            ></div>

            <div className="h-100" style={{ position: "relative", zIndex: 2 }}>
                <div
                    className={`mx-auto justify-content-evenly h-100`}
                    style={{
                        width: "400px",
                        maxWidth: "95vw",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {textareaClicked && (
                        <div ref={topRef}
                            className="d-flex justify-content-center align-items-center mt-2 pt-3"
                            style={{ height: '90px' }}
                        >
                            <div className="text-center" style={{ maxWidth: "70vw" }}>
                                <p
                                    className="lh-sm mx-auto ps-2 m-0"
                                    style={{
                                        fontSize: "clamp(15px, 4.5vw, 17px)",
                                        color: "#fff"
                                    }}
                                >
                                    {t?.hotness?.cardTitle || "What do you think about me?"}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* STEP 1 - Top Section */}
                    <div
                        className={`d-flex flex-column flex-grow-1 ${isIOS ? "my-2" : "my-2"}`}
                        style={{
                            justifyContent: textareaClicked ? "start" : "center",
                            alignContent: textareaClicked ? "end" : "center",
                            transition: 'all 0s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                    >
                        {/* Show skeleton while loading or image not loaded */}
                        {loading || !imageLoaded ? (
                            <CardSkeleton width={cardWidth()} />
                        ) : (
                            <>
                                <div
                                    className={`rounded-4 mx-auto p-0 d-flex flex-column overflow-hidden ${showSuccessModal ? "" : "card-enter"}`}
                                    style={{
                                        width: cardWidth(),
                                        minHeight: textareaClicked ? "240px" : showContent ? isSmallScreen ? "400px" : "470px" : "330px",
                                        boxShadow: "0px 6.44px 8.59px 0px rgba(0, 0, 0, 0.25)",
                                        background: textareaClicked ? "rgba(255, 255, 255, 0.1)" : ``,
                                        transition: 'all 0s cubic-bezier(0.4, 0, 0.2, 1)',
                                    }}
                                >
                                    <div
                                        className={`rounded-4 mx-auto p-0 ${showContent ? "" : "pb-4"} d-flex flex-column overflow-hidden ${textareaClicked && isShaking ? "shake-animation" : ""}`}
                                        style={{
                                            border: textareaClicked ? "1px solid rgba(255, 255, 255, 0.4)" : "3px solid black",
                                            width: "100%",
                                            minHeight: textareaClicked ? "240px" : showContent ? isSmallScreen ? "400px" : "470px" : "330px",
                                            backgroundImage: textareaClicked ? "" : `url(${randomData.CardBg})`,
                                            backgroundRepeat: "no-repeat",
                                            backgroundSize: "cover",
                                            backgroundPosition: "center",
                                            boxShadow: "0px 6.44px 8.59px 0px rgba(0, 0, 0, 0.25)",
                                            position: 'relative',
                                            justifyContent: textareaClicked ? "start" : "space-between",
                                            transition: 'all 0s cubic-bezier(0.4, 0, 0.2, 1)',
                                        }}
                                    >
                                        <div className='w-100 position-relative'
                                            style={{
                                                height: textareaClicked ? "110px" : isSmallScreen ? "160px" : "170px",
                                                background: textareaClicked ? "white" : "transparent"
                                            }}
                                        >
                                            {!textareaClicked && (
                                                <svg viewBox="0 0 318 138" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginTop: "-5px" }}>
                                                    <path d="M158.401 137.277C73.2446 137.277 3.54763 93.7502 -2.05626 90.1595H-2.4209V20.3472C-2.4209 8.54535 6.11304 -1.02231 16.6396 -1.02231H300.578C311.105 -1.02231 319.639 8.54535 319.639 20.3472V90.1595C319.617 90.1738 247.207 137.277 158.401 137.277Z" fill="white" />
                                                    <path d="M158.401 137.277C73.2446 137.277 3.54763 93.7502 -2.05626 90.1595H-2.4209V20.3472C-2.4209 8.54535 6.11305 -1.02231 16.6396 -1.02231H300.578C311.105 -1.02231 319.639 8.54535 319.639 20.3472V90.1595C319.617 90.1738 247.207 137.277 158.401 137.277Z" stroke="#231F20" stroke-width="0.440441" stroke-miterlimit="2.61313" />
                                                </svg>
                                            )}

                                            <span
                                                className={`text-black position-absolute ${textareaClicked ? "text-start GTMedium" : "text-center bricolage-grotesque4"
                                                    }`}
                                                style={{
                                                    top: textareaClicked ? "50%" : "24%",
                                                    left: "50%",
                                                    transform: "translate(-50%, -50%)",
                                                    fontSize: textareaClicked ? "20px" : "18px",
                                                    width: "85%",
                                                    lineHeight: "20px",
                                                    paddingLeft: textareaClicked ? "60px" : "",
                                                    display: "-webkit-box",
                                                    WebkitLineClamp: 3,
                                                    WebkitBoxOrient: "vertical",
                                                    overflow: "hidden"
                                                }}
                                            >
                                                {textareaClicked
                                                    ? translatedName
                                                    : `${t?.common?.que || 'Que:'} ${translatedQuestion2}`
                                                }                                            </span>

                                        </div>

                                        <div className='circle d-flex align-items-center justify-content-center mx-auto'
                                            style={{
                                                position: textareaClicked ? "absolute" : "absolute",
                                                top: textareaClicked ? "38px" : isSmallScreen ? "120px" : "135px",
                                                left: textareaClicked ? "40px" : "50%",
                                                transform: textareaClicked ? "translate(-50%, -50%)" : "translate(-50%, -50%)",
                                                background: textareaClicked ? "#f2f2f2" : "white",
                                                border: "0.5px solid #353435",
                                                width: textareaClicked ? "55px" : isSmallScreen ? "100px" : "115px",
                                                height: textareaClicked ? "55px" : isSmallScreen ? "100px" : "115px",
                                                transition: 'all 0s cubic-bezier(0.4, 0, 0.2, 1)',
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: "85%",
                                                    height: "85%",
                                                    overflow: "hidden",
                                                    borderRadius: "inherit", // important for shapes
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: "100%",
                                                        height: "100%",
                                                        backgroundImage: `url(${emojiImage})`,
                                                        backgroundSize: "cover",
                                                        backgroundPosition: "center",
                                                        backgroundRepeat: "no-repeat",
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {!textareaClicked && (
                                            <div
                                                className="flex-grow d-flex align-items-center justify-content-center mx-auto "
                                                style={{
                                                    backgroundImage: `url(${"https://lol-image-bucket.s3.ap-south-1.amazonaws.com/images/question6/whitecard.png"})`,
                                                    backgroundSize: isSmallScreen ? "100% 100%" : "100% 100%",
                                                    backgroundRepeat: "no-repeat",
                                                    backgroundPosition: "center",
                                                    height: showContent ? "60px" : "70px",
                                                    width: "80%",
                                                }}
                                            >
                                                <span className="text-black bricolage-grotesque px-5 text-center" style={{ fontSize: "18px" }}>
                                                    {translatedName}
                                                </span>
                                            </div>
                                        )}


                                        {showContent && (
                                            <>
                                                {textareaClicked ? (
                                                    <div className='p-2 fw-semibold text-start d-flex flex-column GTRegular' style={{ height: "100%" }}>
                                                        <textarea
                                                            ref={textareaRef}
                                                            placeholder={`${t?.hotness?.cardPlaceholder || "Write your thoughts..."} `}
                                                            value={comment}
                                                            onKeyDown={handleKeyDown}
                                                            onChange={handleTextChange}
                                                            onFocus={handleFocus}
                                                            onBlur={handleBlur}
                                                            className='border-0 flex-grow-1 custom-placeholder-9'
                                                            style={{
                                                                resize: 'none',
                                                                backgroundColor: 'transparent',
                                                                outline: 'none',
                                                                boxShadow: 'none',
                                                                color: 'white',
                                                                fontSize: '17px',
                                                                fontWeight: '400',
                                                                lineHeight: '1.5',
                                                                caretColor: "white",
                                                            }}
                                                            enterKeyHint="done"
                                                            inputMode="text"
                                                            autoFocus
                                                        />


                                                        <DiceButton
                                                            category={category}
                                                            subCategory={reputationKey}
                                                            onContentFetched={(text) => setComment(text)}
                                                            disabled={isUploading || isCheckingConnection}
                                                        />
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div className="d-flex align-items-center justify-content-center position-relative p-2 bg-white mb-3 mx-auto rounded-4"
                                                            style={{ minHeight: "100px", width: "90%", border: "2px solid black" }}>
                                                            {comment ? (
                                                                <Row className="w-100 position-relative align-items-center h-100">
                                                                    <Col
                                                                        xs={11}
                                                                        onClick={handleEditClick}
                                                                        className="p-2 cursor bricolage-grotesque"
                                                                        style={{
                                                                            fontSize: '18px',
                                                                            height: 'auto',
                                                                            wordBreak: 'break-all',
                                                                            overflowWrap: 'anywhere',
                                                                            whiteSpace: 'pre-wrap',
                                                                            lineHeight: "19px",
                                                                            textAlign: 'center',
                                                                        }}
                                                                    >
                                                                        {comment}
                                                                    </Col>

                                                                    <button
                                                                        onClick={handleEditClick}
                                                                        disabled={isUploading || isCheckingConnection}
                                                                        className="btn btn-link p-0 position-absolute"
                                                                        style={{
                                                                            bottom: "0px",
                                                                            right: "0px",
                                                                            fontSize: '18px',
                                                                            color: '#666',
                                                                            cursor: isUploading || isCheckingConnection ? 'not-allowed' : 'pointer',// ✅ ADDED
                                                                            opacity: "1"
                                                                        }}
                                                                    >
                                                                        <div
                                                                            className='rounded-circle overflow-hidden d-flex justify-content-center align-items-center ms-auto'
                                                                            style={{ width: 28, height: 28, background: "#F3F3F3", border: "1px solid black" }}
                                                                        >
                                                                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                                <path d="M0.281899 10.0906L0.00598075 12.3747C-0.00693942 12.4719 0.00112984 12.5708 0.0296453 12.6647C0.0581607 12.7586 0.10646 12.8452 0.17129 12.9189C0.236121 12.9925 0.315977 13.0514 0.40548 13.0916C0.494984 13.1317 0.592056 13.1523 0.69016 13.1518C0.719384 13.1518 0.748581 13.15 0.777567 13.1463L3.06164 12.8704C3.54182 12.8095 3.98802 12.5904 4.32971 12.2476L10.2481 6.32914C10.2995 6.27776 10.3283 6.20808 10.3283 6.13542C10.3283 6.06277 10.2995 5.99309 10.2481 5.9417L7.21165 2.90249C7.16027 2.85112 7.09059 2.82227 7.01793 2.82227C6.94528 2.82227 6.8756 2.85112 6.82421 2.90249L0.904703 8.82255C0.561886 9.16424 0.342737 9.61044 0.281899 10.0906Z" fill="black" />
                                                                                <path d="M12.7366 1.83298L11.3199 0.416277C11.188 0.284306 11.0314 0.179617 10.859 0.10819C10.6866 0.0367636 10.5019 0 10.3153 0C10.1287 0 9.94396 0.0367636 9.77159 0.10819C9.59923 0.179617 9.44262 0.284306 9.31073 0.416277L7.98062 1.74638C7.92925 1.79777 7.90039 1.86746 7.90039 1.94012C7.90039 2.01278 7.92925 2.08247 7.98062 2.13385L11.019 5.17222C11.0704 5.22359 11.1401 5.25245 11.2127 5.25245C11.2854 5.25245 11.3551 5.22359 11.4065 5.17222L12.7366 3.84211C12.8685 3.71022 12.9732 3.55361 13.0447 3.38125C13.1161 3.20888 13.1528 3.02412 13.1528 2.83754C13.1528 2.65096 13.1161 2.46621 13.0447 2.29384C12.9732 2.12147 12.8685 1.96487 12.7366 1.83298Z" fill="black" />
                                                                                <path d="M13.1519 12.6046C13.1519 12.4593 13.0942 12.3199 12.9914 12.2171C12.8886 12.1144 12.7493 12.0566 12.6039 12.0566H6.30191C6.15657 12.0566 6.01718 12.1144 5.91441 12.2171C5.81164 12.3199 5.75391 12.4593 5.75391 12.6046C5.75391 12.75 5.81164 12.8894 5.91441 12.9921C6.01718 13.0949 6.15657 13.1526 6.30191 13.1526H12.6039C12.7493 13.1526 12.8886 13.0949 12.9914 12.9921C13.0942 12.8894 13.1519 12.75 13.1519 12.6046Z" fill="black" />
                                                                            </svg>

                                                                        </div>
                                                                    </button>
                                                                </Row>
                                                            ) : (
                                                                <div className="w-100 position-relative h-100 d-flex align-items-center justify-content-center">
                                                                    <textarea
                                                                        className="form-control custom-placeholder-9 border-0 text-center bricolage-grotesque6"
                                                                        // placeholder={`${t?.hotness?.cardPlaceholder || "Write your thoughts..."} `}
                                                                        rows="1"
                                                                        style={{
                                                                            resize: 'none',
                                                                            fontSize: '16px',
                                                                            background: 'transparent',
                                                                            textAlign: 'center',
                                                                        }}
                                                                        onClick={handleTextareaClick}
                                                                        readOnly
                                                                    />

                                                                    <p className='w-100 text-center d-flex align-items-center justify-content-center bricolage-grotesque6'
                                                                        style={{
                                                                            position: 'absolute',
                                                                            left: '0px',
                                                                            top: '50%',
                                                                            fontSize: '16px',
                                                                            transform: 'translateY(-50%)',
                                                                            pointerEvents: 'none',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            color: '#999',
                                                                        }}
                                                                    >
                                                                        {t?.hotness?.cardPlaceholder || "Write your thoughts"}...
                                                                        <svg width="13" height="13" viewBox="0 0 7 7" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ paddingLeft: "2px" }}>
                                                                            <path d="M0.142444 5.09848L0.00302207 6.25262C-0.00350649 6.30176 0.00057091 6.35173 0.0149798 6.39917C0.0293886 6.4466 0.0537942 6.49039 0.086553 6.5276C0.119312 6.5648 0.159663 6.59456 0.204889 6.61486C0.250115 6.63516 0.299166 6.64553 0.348738 6.64527C0.363505 6.64531 0.378258 6.64438 0.392904 6.64251L1.54704 6.50308C1.78968 6.47234 2.01515 6.36161 2.1878 6.18838L5.17838 3.19781C5.20433 3.17184 5.21892 3.13663 5.21892 3.09992C5.21892 3.06321 5.20433 3.028 5.17838 3.00203L3.64405 1.46632C3.61808 1.44036 3.58287 1.42578 3.54616 1.42578C3.50945 1.42578 3.47424 1.44036 3.44828 1.46632L0.457146 4.45772C0.283921 4.63038 0.173185 4.85585 0.142444 5.09848Z" fill="#999999" />
                                                                            <path d="M6.43542 0.926203L5.71956 0.210345C5.65291 0.14366 5.57378 0.0907603 5.48668 0.0546685C5.39959 0.0185767 5.30623 0 5.21195 0C5.11767 0 5.02432 0.0185767 4.93722 0.0546685C4.85012 0.0907603 4.77099 0.14366 4.70434 0.210345L4.03224 0.882448C4.00628 0.908414 3.9917 0.943627 3.9917 0.980342C3.9917 1.01706 4.00628 1.05227 4.03224 1.07824L5.56753 2.61352C5.59349 2.63948 5.6287 2.65406 5.66542 2.65406C5.70214 2.65406 5.73735 2.63948 5.76331 2.61352L6.43542 1.94142C6.5021 1.87477 6.555 1.79564 6.59109 1.70854C6.62719 1.62145 6.64576 1.52809 6.64576 1.43381C6.64576 1.33953 6.62719 1.24618 6.59109 1.15908C6.555 1.07198 6.5021 0.992849 6.43542 0.926203Z" fill="#999999" />
                                                                            <path d="M6.64593 6.36675C6.64593 6.29331 6.61676 6.22288 6.56483 6.17095C6.5129 6.11902 6.44247 6.08984 6.36903 6.08984H3.18462C3.11118 6.08984 3.04075 6.11902 2.98882 6.17095C2.93689 6.22288 2.90771 6.29331 2.90771 6.36675C2.90771 6.44019 2.93689 6.51062 2.98882 6.56255C3.04075 6.61448 3.11118 6.64365 3.18462 6.64365H6.36903C6.44247 6.64365 6.5129 6.61448 6.56483 6.56255C6.61676 6.51062 6.64593 6.44019 6.64593 6.36675Z" fill="#999999" />
                                                                        </svg>
                                                                    </p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>

                                {textareaClicked && (
                                    <>
                                        <p className={`text-end text-white mt-2 pe-4 GTMedium mb-0`}>
                                            {convertToNativeNumerals(typedChars, lanText)}/{convertToNativeNumerals(maxLength, lanText)}
                                        </p>

                                        <BadWordWarning
                                            visible={hasBadWord}
                                            message={
                                                t?.common?.badWordWarning ||
                                                "Avoid using offensive language"
                                            }
                                        />
                                    </>
                                )}
                            </>
                        )}
                    </div>

                    {!textareaClicked && !loading && imageLoaded && showContentButton && (
                        <div className='rounded-circle mx-auto d-flex align-items-center justify-content-center'
                            // onClick={() => setShowContent(!showContent)}
                            onPointerDown={(e) => {
                                e.preventDefault();
                                handleButtonPress();
                                setShowContent(!showContent)
                            }}
                            onPointerUp={handleButtonRelease}
                            onPointerLeave={handleButtonRelease}
                            style={{
                                width: isSmallScreen ? "50px" : "55px",
                                height: isSmallScreen ? "50px" : "55px",
                                marginBottom: isSmallScreen ? "12px" : "20px",
                                background: `
                                radial-gradient(
                                    circle at center,
                                    rgba(255,255,255,0.25) 0%,
                                    rgba(255,255,255,0.15) 40%,
                                    rgba(255,255,255,0.08) 70%,
                                    rgba(255,255,255,0.03) 100%
                                     )
                                        `,
                                backdropFilter: "blur(10px)",
                                WebkitBackdropFilter: "blur(10px)",
                                boxShadow: "0px 2px 8px rgba(0,0,0,0.07), inset 0 2px 2px rgba(255,255,255,0.3), inset 0 -1px 1px rgba(255,255,255,0.2)",
                                transform: isButtonPressed ? 'scale(0.92)' : 'scale(1)',
                                transition: 'all 0.1s ease',
                            }}
                        >
                            <svg width="35" height="35" viewBox="0 0 31 31" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M25.174 25.5472C23.4064 27.2505 20.6131 27.1989 15.0268 27.0957C9.44046 26.9925 6.64727 26.9409 4.94387 25.1734C3.24047 23.4058 3.29208 20.6126 3.3953 15.0263C3.49852 9.4399 3.55013 6.64671 5.31765 4.94331C7.08518 3.23991 9.87837 3.29152 15.4647 3.39474C21.0511 3.49796 23.8443 3.54957 25.5477 5.31709C27.2511 7.08463 27.1995 9.87781 27.0963 15.4642C26.993 21.0505 26.9414 23.8437 25.174 25.5472Z" stroke="white" stroke-width="1.15252" stroke-linecap="round" stroke-linejoin="round" />
                                <path d="M11.2847 27.0271L11.7227 3.32617" stroke="white" stroke-width="1.15252" />
                                <path d="M27.0966 15.4639L11.5039 15.1758" stroke="white" stroke-width="1.15252" />
                            </svg>
                        </div>
                    )}

                    {!textareaClicked && (
                        <div className='mx-auto mt-auto' style={{ width: "90%", marginBottom: isSmallScreen ? "5px" : "6px" }}>
                            {loading || !imageLoaded ? (
                                // Button Skeleton
                                <div
                                    className="rounded-pill mx-auto shimmer-element"
                                    style={{
                                        width: "100%",
                                        height: "55px",
                                        transform: "scale(0.95)",
                                    }}
                                />
                            ) : (
                                <Button
                                    className="rounded-pill mx-auto GTMedium text-white border-0"
                                    style={{
                                        width: "100%",
                                        padding: "14px 1px",
                                        height: "56px",
                                        cursor: (!isUploading && (comment.trim() || !showContent)) ? 'pointer' : 'not-allowed',
                                        background: "black",
                                        transform: "scale(0.95)",
                                        opacity: (comment.trim() || !showContent) ? 1 : 0.5,
                                        transition: 'transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease',
                                    }}
                                    onClick={handleSendClick}
                                    disabled={isUploading || !(comment.trim() || !showContent) || isCheckingConnection}
                                >
                                    {isUploading || isCheckingConnection ? (
                                        <>
                                            <div className="custom-spinner" style={{ borderTopColor: "#fff" }} />
                                        </>
                                    ) : (
                                        <span className='GTMedium'
                                            style={{
                                                fontSize: "clamp(21px, 4.5vw, 27px)",
                                                lineHeight: 1,
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {t?.common?.send || "Send"}
                                        </span>
                                    )}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>


            <RotatePhonePopup
                excludeWhen={[keyboardOpen]}
            />

            <LanguageDropdown />

            <CompletePopup
                showSuccessModal={showSuccessModal}
                toggle={handleModalClose}
                data={originalData}
                username={username}
                category={category}
                style={{ zIndex: 10000 }}
            />
        </div>
    );
};

export default ReputationCard;